import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import type { Medication } from "@/shared/types";

// The backend's MedicationRequest fields are free-text from whatever the source
// EHR sent (e.g. "Oral", "Take 1 tablet by mouth twice daily") — none of them are
// the closed union values MedicationsSection expects, so this maps by keyword
// match rather than exact equality, and always falls back to a safe default
// rather than throwing on data we don't recognize.

/** YYYY-MM-DD, or "" when the source gave no usable date — never a made-up
 *  one (it used to fall back to 1970-01-01, and a non-ISO value like
 *  "05/12/2024" rendered as "undefined NaN, NaN"). */
function toDateOnly(value: unknown): string {
  const str = typeof value === "string" ? value.trim() : "";
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(str);
  if (iso) return iso[1];
  const parsed = str ? new Date(str) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

function normalizeRoute(value: unknown): Medication["route"] {
  const str = typeof value === "string" ? value.toLowerCase() : "";
  if (str.includes("oral") || str.includes("mouth")) return "oral";
  if (str.includes("topical") || str.includes("skin")) return "topical";
  if (str.includes("inject") || str.includes("intramuscular") || str.includes("subcutaneous") || str.includes("intravenous")) return "injection";
  if (str.includes("inhal")) return "inhalation";
  return "other";
}

function normalizeStatus(value: unknown): Medication["status"] {
  const str = typeof value === "string" ? value.toLowerCase() : "";
  if (str.includes("discontinu")) return "discontinued";
  if (str.includes("hold")) return "on_hold";
  if (str.includes("complet")) return "completed";
  // "Active", "", or anything unrecognized — a med we can't classify is safer
  // shown as active (draws attention) than silently filed as discontinued.
  return "active";
}

export function adaptMedication(record: ParsedRecord): Medication {
  const f = record.fields;
  const name = (f.medication_name as string) || (f.medication_codeable_text as string) || "Unnamed medication";
  const doseQuantity = f.dose_quantity != null ? String(f.dose_quantity) : "";
  const doseUnit = typeof f.dose_unit === "string" ? f.dose_unit : "";
  const dosage = [doseQuantity, doseUnit].filter(Boolean).join(" ") || "Not specified";
  // requester_ref is "Practitioner/<id>" — no practitioner-name lookup wired on
  // the frontend yet, so this shows the raw id rather than a resolved name.
  const prescriberRef = typeof f.requester_ref === "string" ? f.requester_ref : "";
  const prescribedBy = prescriberRef.replace(/^Practitioner\//, "") || "Unknown provider";
  const authoredOn = toDateOnly(f.authored_on);
  const repeatsAllowed = f.dispense_repeats_allowed;

  return {
    id: record.id,
    name,
    dosage,
    frequency: typeof f.frequency === "string" ? f.frequency : "Not specified",
    route: normalizeRoute(f.route),
    prescribedBy,
    prescribedDate: authoredOn,
    startDate: authoredOn,
    // A clinician marking it discontinued (Module 5) puts it in Past whatever the source said.
    status: record.reviewOutcome?.effect === "DISCONTINUED" ? "discontinued" : normalizeStatus(f.status),
    purpose: typeof f.reason === "string" && f.reason ? f.reason : "Not specified",
    instructions: typeof f.dosage_text === "string" && f.dosage_text ? f.dosage_text : "Follow your provider's instructions.",
    // null (not 0) when the source data never reported a refill count —
    // coercing to 0 used to misclassify a record with NO refill data as
    // "confirmed almost out of refills," incorrectly qualifying it for the
    // "Low on Refills" list on top of its normal "All" listing.
    refillsRemaining: typeof repeatsAllowed === "number" ? repeatsAllowed : null,
    review: record.review ?? null,
    origin: record.origin ?? null,
    sourceVendor: record.sourceVendor,
    reviewEffect: record.reviewOutcome?.effect ?? null,
    correctedFields: record.correctedFields,
    duplicates: record.duplicates ?? null,
    earlier: record.reviewBaseline ?? false,
    rxcui: record.rxcui ?? null,
  };
}
